import { formatNumber } from '@eformulieren/engine'
import type { DocumentVerwijzing } from '@eformulieren/model'
import { type ReactNode, useState } from 'react'
import { word } from '../../language/words.js'
import { type DocumentsProps, type UploadOutcome } from './kinds.js'
import { fileReferenceOf } from './hash.js'
import { extent } from './dossierValue.js'
import { Spinner } from '../../shell/Spinner.js'

/**
 * Bijlagen en beeld: `file` en `image`.
 *
 * **Een bijlage is een verwijzing en nooit het bestand** (U25, R-199). De browser
 * rekent de SHA-256 zelf uit, dus wat in de stand belandt is hash plus bestandsnaam
 * plus omvang: precies wat de evaluator nodig heeft om de bestandseisen te toetsen
 * (R-104) en wat een revisie over jaren nog leesbaar maakt.
 *
 * **Vervangen is toevoegen en de oude weghalen** (R-200): het bestand blijft bestaan
 * zolang een revisie ernaar wijst, dus deze control verwijdert nooit iets uit de
 * opslag; hij haalt alleen de verwijzing uit de werkstand.
 *
 * **Het filter is comfort en geen poort.** `accept` staat op de toegestane typen
 * zodat de bestandskiezer meteen het juiste laat zien, maar de toets blijft de
 * regelsoort: een invuller kan het filter omzeilen en dan hoort er een melding uit
 * de regel te komen (R-30) op het moment van R-25, niet een stille weigering.
 *
 * **Met `upload` gaat het bestand naar de opslag** (R-104, U25). Wat nooit kan slagen
 * (een type of grootte die het veld niet toestaat, een inhoud die niet is wat de naam
 * zegt, een macro, een versleuteld bestand) weigeren de pod en de opslag meteen, en dan
 * komt er geen verwijzing in de stand: een verwijzing naar een bestand dat er niet is,
 * wees in het bericht nergens heen. De weigering staat bij het veld, per bestand, als
 * melding die een schermlezer voorleest (R-139, R-140), en het veld beschrijft zich er
 * ook mee. Het aantal blijft aan de regel: wie er een te veel kiest, krijgt haar melding
 * en haalt er een weg.
 *
 * **Downloaden zit er nog niet in.** Ontwerp 6.2 noemt downloaden en verwijderen als
 * rij-acties; downloaden vraagt de bytes en die staan in de content-addressed opslag
 * die in M3 komt. Een knop die nooit werkt is erger dan geen knop, dus hij komt met
 * de opslag.
 */

export interface AttachmentsProps extends DocumentsProps {
  readonly soort: 'file' | 'image'
}

export function Attachments({
  taal: language,
  koppeling: link,
  documenten: documents,
  onDocuments,
  bewerkbaar: editable,
  requirements,
  soort: kind,
  upload,
}: AttachmentsProps): ReactNode {
  const [busy, setBusy] = useState(false)
  /** Of er nu een bestand boven de zone hangt, voor de markering (alleen visueel). */
  const [over, setOver] = useState(false)
  const [refusals, setRefusals] = useState<readonly { readonly naam: string; readonly code: string }[]>([])
  const refusalId = `${link.id}-weigering`

  const add = async (files: FileList | null): Promise<void> => {
    if (files === null || files.length === 0) return
    setBusy(true)
    setRefusals([])
    try {
      const nextOptions: DocumentVerwijzing[] = []
      const refused: { naam: string; code: string }[] = []
      for (const file of files) {
        if (upload === undefined) {
          nextOptions.push(await fileReferenceOf(file))
          continue
        }
        let outcome: UploadOutcome
        try {
          outcome = await upload(file)
        } catch {
          outcome = { weigering: 'geen-verbinding' }
        }
        if ('verwijzing' in outcome) nextOptions.push(outcome.verwijzing)
        else refused.push({ naam: file.name, code: outcome.weigering })
      }
      setRefusals(refused)
      // Dezelfde inhoud twee keer aanhangen levert dezelfde hash op; dat is één
      // bestand en geen twee, dus de verwijzing komt er niet dubbel in.
      const existing = new Set(documents.map((d) => d.hash))
      const added = nextOptions.filter((d) => !existing.has(d.hash))
      if (added.length > 0) onDocuments([...documents, ...added])
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ef-bijlagen">
      {documents.length === 0 ? null : (
        <ul className="ef-bijlagen__lijst">
          {documents.map((document) => (
            <li className="ef-bijlage" key={document.hash}>
              <span className="ef-bijlage__naam">{document.bestandsnaam}</span>
              <span className="ef-bijlage__omvang">{extent(document.grootteBytes, language)}</span>
              {editable ? (
                <button
                  type="button"
                  className="ef-bijlage__weg"
                  aria-label={
                    language === 'nl'
                      ? `Verwijder ${document.bestandsnaam}`
                      : `Remove ${document.bestandsnaam}`
                  }
                  onClick={() => onDocuments(documents.filter((d) => d.hash !== document.hash))}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editable ? (
        <>
          {/*
            Een eigen zone in plaats van de kale knop van de browser (ontwerp van het invulscherm,
            29 oktober 2026): die zei "Choose File" in de taal van de browser en niet van het
            formulier. Het echte bestandsveld blijft staan, alleen visueel verborgen: het houdt de
            focus, het label van het veld en de koppeling met de hulptekst (R-140), en Enter of
            spatie opent de kiezer zoals altijd. De knop is het label ervan, dus klikken opent
            dezelfde kiezer. Slepen komt op dezelfde plek uit als kiezen (`add`).
          */}
          <div
            className="ef-bijlagen__zone"
            data-boven={over ? 'ja' : undefined}
            onDragOver={(event) => {
              if (busy) return
              event.preventDefault()
              setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
              event.preventDefault()
              setOver(false)
              if (!busy) void add(event.dataTransfer.files)
            }}
          >
            <input
              className="ef-bijlagen__kiezer"
              type="file"
              {...link}
              aria-describedby={refusals.length === 0 ? link['aria-describedby'] : [link['aria-describedby'], refusalId].filter((id) => id !== undefined && id !== '').join(' ')}
              multiple={requirements === undefined || requirements.maximumAantal > 1}
              accept={
                requirements === undefined
                  ? kind === 'image'
                    ? 'image/*'
                    : undefined
                  : requirements.typen.map((type) => `.${type}`).join(',')
              }
              disabled={busy}
              onChange={(event) => {
                void add(event.target.files)
                // De kiezer leegmaken, anders levert hetzelfde bestand opnieuw kiezen
                // geen change-gebeurtenis op.
                event.target.value = ''
              }}
            />
            <label className="ef-bijlagen__knop" htmlFor={link.id}>
              {language === 'nl'
                ? requirements !== undefined && requirements.maximumAantal === 1
                  ? 'Kies een bestand'
                  : 'Kies bestanden'
                : requirements !== undefined && requirements.maximumAantal === 1
                  ? 'Choose a file'
                  : 'Choose files'}
            </label>
            <span className="ef-bijlagen__sleep" aria-hidden="true">
              {requirements !== undefined && requirements.maximumAantal === 1
                ? language === 'nl'
                  ? 'of sleep het hierheen'
                  : 'or drag it here'
                : language === 'nl'
                  ? 'of sleep ze hierheen'
                  : 'or drag them here'}
            </span>
          </div>
          {requirements === undefined ? null : (
            <p className="ef-bijlagen__eisen">{requirementsText(requirements, language)}</p>
          )}
          {busy ? (
            <p className="ef-bijlagen__bezig">
              <Spinner />{' '}
              {word('bezig', language)}
            </p>
          ) : null}
          {refusals.length === 0 ? null : (
            <div id={refusalId} role="alert">
              <ul className="ef-veld__meldingen">
                {refusals.map((refusal) => (
                  <li className="ef-veld__melding ef-veld__melding--hard" key={refusal.naam}>
                    {refusalText(refusal.naam, refusal.code, language)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

function requirementsText(
  requirements: { typen: readonly string[]; maximumAantal: number; maximumGrootteMb: number },
  language: 'nl' | 'en',
): string {
  const types = requirements.typen.join(', ')
  // Eén bestand in het enkelvoud: "Maximaal 1 bestanden" stond er tot het ontwerp van het
  // invulscherm.
  if (requirements.maximumAantal === 1) {
    return language === 'nl'
      ? `Eén bestand, hoogstens ${formatNumber(requirements.maximumGrootteMb, 'nl')} MB. Toegestaan: ${types}.`
      : `One file, up to ${formatNumber(requirements.maximumGrootteMb, 'en')} MB. Allowed: ${types}.`
  }
  return language === 'nl'
    ? `Maximaal ${formatNumber(requirements.maximumAantal, 'nl')} bestanden, elk hoogstens ${formatNumber(requirements.maximumGrootteMb, 'nl')} MB. Toegestaan: ${types}.`
    : `At most ${formatNumber(requirements.maximumAantal, 'en')} files, each up to ${formatNumber(requirements.maximumGrootteMb, 'en')} MB. Allowed: ${types}.`
}

/**
 * Waarom een bestand niet werd aangenomen, per code van de pod of de opslag, in de taal van het formulier.
 * Wat de invuller kan doen staat erbij; wat er technisch mis is, staat in het log van de pod.
 */
const REFUSALS: Readonly<Record<string, { readonly nl: string; readonly en: string }>> = {
  'type-niet-voor-dit-veld': { nl: 'dit type bestand neemt dit veld niet aan', en: 'this field does not accept this type of file' },
  'type-niet-toegestaan': { nl: 'dit type bestand neemt het formulier niet aan', en: 'the form does not accept this type of file' },
  'inhoud-wijkt-af': {
    nl: 'de inhoud is niet wat de naam zegt, of het bestand is niet te lezen; sla het opnieuw op als het type dat de naam noemt',
    en: 'the content is not what the name says, or the file cannot be read; save it again as the type its name gives',
  },
  'actieve-inhoud': {
    nl: 'het bestand bevat een macro, een script of een ingesloten object; sla het op zonder',
    en: 'the file contains a macro, a script or an embedded object; save it without',
  },
  versleuteld: { nl: 'het bestand is versleuteld of met een wachtwoord beveiligd; lever het zonder beveiliging', en: 'the file is encrypted or password protected; provide it without protection' },
  'te-groot-voor-dit-veld': { nl: 'het bestand is te groot voor dit veld', en: 'the file is too large for this field' },
  'te-groot': { nl: 'het bestand is te groot', en: 'the file is too large' },
  'geen-inhoud': { nl: 'het bestand is leeg', en: 'the file is empty' },
  'beurt-al-gesloten': { nl: 'dit formulier is al ingediend', en: 'this form has already been submitted' },
  'geen-verbinding': { nl: 'er is geen verbinding; probeer het zo opnieuw', en: 'there is no connection; please try again shortly' },
}

function refusalText(fileName: string, code: string, language: 'nl' | 'en'): string {
  const reason = REFUSALS[code]?.[language] ?? (language === 'nl' ? 'het bestand is niet aangenomen; probeer het opnieuw of neem contact op' : 'the file was not accepted; try again or get in touch')
  return language === 'nl' ? `${fileName} is niet toegevoegd: ${reason}.` : `${fileName} was not added: ${reason}.`
}
